import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectDetails } from '../data/projectDetails';
import { siteData } from '../data/mockData';
import { ArrowLeft, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';

const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
      >
        <X size={32} />
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white/50 text-sm font-medium z-10">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous */}
      {images.length > 1 && (
        <button
          onClick={onPrev}
          className="absolute left-4 md:left-8 text-white/50 hover:text-white transition-colors z-10"
        >
          <ChevronLeft size={48} />
        </button>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={images[currentIndex]}
          alt=""
          className="max-w-full max-h-[85vh] object-contain"
          style={{ transition: 'opacity 0.3s ease' }}
        />
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          onClick={onNext}
          className="absolute right-4 md:right-8 text-white/50 hover:text-white transition-colors z-10"
        >
          <ChevronRight size={48} />
        </button>
      )}
    </div>
  );
};

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { t, get } = useTranslation(lang);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const project = projectDetails[slug];
  const allProjectSlugs = siteData.projectsPage.allProjects.map(p => p.slug);
  const currentIdx = allProjectSlugs.indexOf(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoaded(false);
    setTimeout(() => setLoaded(true), 100);
  }, [slug]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const nextImage = useCallback(() => {
    if (project) setLightboxIndex((prev) => (prev + 1) % project.gallery.length);
  }, [project]);

  const prevImage = useCallback(() => {
    if (project) setLightboxIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
  }, [project]);

  // Get 3 related projects (next in the list)
  const getRelated = () => {
    const related = [];
    for (let i = 1; i <= 3; i++) {
      const idx = (currentIdx + i) % siteData.projectsPage.allProjects.length;
      related.push(siteData.projectsPage.allProjects[idx]);
    }
    return related;
  };

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{get(t.projectDetailExtra.notFound)}</h2>
          <button onClick={() => navigate('/projecten')} className="text-[#A855F7] font-bold hover:underline">
            {get(t.projectDetail.back)}
          </button>
        </div>
      </div>
    );
  }

  const related = getRelated();

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      {/* Noise texture */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '256px 256px',
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header area */}
        <div className="pt-28 md:pt-36 pb-8 px-8 md:px-16 lg:px-24">
          <button
            onClick={() => navigate('/projecten')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            {get(t.projectDetail.back)}
          </button>

          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight italic"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {project.title}
          </h1>
        </div>

        {/* Hero image (first gallery image, full width) */}
        {project.gallery.length > 0 && (
          <div
            className="px-8 md:px-16 lg:px-24 mb-12"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
            }}
          >
            <div
              className="relative overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(0)}
            >
              <img
                src={project.gallery[0]}
                alt={project.title}
                className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                style={{ maxHeight: '70vh' }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center">
                <span className="text-white text-sm font-bold tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {get(t.projectDetail.viewPhoto)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <div
          className="px-8 md:px-16 lg:px-24 mb-16 max-w-4xl"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s',
          }}
        >
          {project.description.map((p, i) => {
            // Check for bold headers like **De vraag**
            const parts = p.split(/\*\*(.*?)\*\*/g);
            return (
              <div key={i} className="mb-6">
                {parts.map((part, j) =>
                  j % 2 === 1 ? (
                    <h3 key={j} className="text-xl md:text-2xl font-bold text-white mb-3 mt-8">
                      {part}
                    </h3>
                  ) : (
                    part.split('\n\n').map((line, k) => (
                      <p key={`${j}-${k}`} className="text-gray-300 text-base md:text-lg leading-relaxed mb-4">
                        {line}
                      </p>
                    ))
                  )
                )}
              </div>
            );
          })}
        </div>

        {/* Gallery grid */}
        {project.gallery.length > 1 && (
          <div className="px-8 md:px-16 lg:px-24 mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {project.gallery.slice(1).map((img, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden cursor-pointer group ${
                    i === project.gallery.length - 2 && (project.gallery.length - 1) % 2 !== 0
                      ? 'md:col-span-2'
                      : ''
                  }`}
                  onClick={() => openLightbox(i + 1)}
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(30px)',
                    transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${0.4 + i * 0.1}s`,
                  }}
                >
                  <img
                    src={img}
                    alt={`${project.title} ${i + 2}`}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inspiratie section */}
        <div className="px-8 md:px-16 lg:px-24 py-16 md:py-24">
          <div
            className="h-[1px] mb-16"
            style={{ background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.3), transparent)' }}
          />

          <h2 className="text-3xl md:text-4xl font-black text-white italic mb-12">
            {get(t.projectDetail.inspiration)}
          </h2>

          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/projecten')}
              className="inline-block border-2 text-white px-6 py-2.5 text-sm font-bold tracking-[0.15em] uppercase transition-all duration-500"
              style={{ borderColor: '#A855F7' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#A855F7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {get(t.projectDetail.allProjects)}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((rp) => (
              <div
                key={rp.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/projecten/${rp.slug}`)}
              >
                <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
                  <img
                    src={rp.image}
                    alt={rp.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <h3 className="mt-3 text-lg font-bold text-white uppercase tracking-wider">
                  {rp.title}
                </h3>
                <p className="text-sm text-gray-400 mt-1">{get(t.projectDetail.readMore)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="px-8 md:px-16 lg:px-24 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white italic mb-8 max-w-2xl mx-auto">
            {get(t.projectDetail.ctaHeading)}
          </h2>
          <button
            onClick={() => { navigate('/contact'); window.scrollTo({top:0}); }}
            className="inline-flex items-center gap-3 border-2 text-white px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-500 group"
            style={{ borderColor: '#A855F7' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#A855F7'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {get(t.cta.button)}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={project.gallery}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
